import React, { useRef, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import { RefreshCw, Download, ChevronDown, Loader2, ArrowLeft } from 'lucide-react';
import imageILogo from '../../assets/images/image-i-logo.png';
import configData from '../../config';
import { getUserAccountId } from '../lib/access';

export function InsightHubReportPage() {
  const history   = useHistory();
  const reportRef = useRef(null);
  const customization = useSelector(s => s.customization);
  const org_id = useSelector(s => s?.account?.org?.id || s?.account?.org_data?.data?.id);
  const account = useSelector(s => s.account || {});
  const user_id = getUserAccountId(account);

  const [report,       setReport]       = useState({ title:'', embedUrl:'', isSecure:false, reportId:'', accessToken:'', datasetId:'', groupId:'' });
  const [loaded,       setLoaded]       = useState(false);
  const [menuOpen,     setMenuOpen]     = useState(false);

  // Sync from Redux when payload changes
  useEffect(() => {
    if (!customization.payload) return;
    const { title, embedUrl, reportData, reportId, isSecure, accessToken, datasetId, groupId } = customization.payload;
    setLoaded(false);
    setReport({
      title:       title       || '',
      embedUrl:    embedUrl    || reportData || '',
      reportId:    reportId    || '',
      isSecure:    isSecure    || false,
      accessToken: accessToken || '',
      datasetId:   datasetId   || '',
      groupId:     groupId     || '',
    });
  }, [customization.payload]);

  const download = async (format) => {
    if (!report.reportId) return;
    try {
      const token = localStorage.getItem('token');
      const endpoint = format === 'PDF' ? 'download-report-snapshot' : 'download-report';
      const formatQuery = format === 'PDF' ? '' : `&format=${format}`;
      const params = new URLSearchParams({
        report_id: String(report.reportId),
        org_id: String(org_id),
      });

      if (format === 'PDF') {
        params.set('title', report.title || 'IMAGE-I Report');
        params.set('embed_url', report.embedUrl || report.reportData || '');
        params.set('is_secure', report.isSecure ? '1' : '0');
        if (user_id) params.set('user_id', String(user_id));
        if (report.groupId) params.set('group_id', String(report.groupId));
        if (report.datasetId) params.set('dataset_id', String(report.datasetId));
      }

      const res = await fetch(
        `${configData.API_SERVER}user/${endpoint}/?${params.toString()}${formatQuery}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!res.ok) return;
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${report.title || 'report'}.${format.toLowerCase()}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { console.error(e); }
    setMenuOpen(false);
  };

  const refresh = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${configData.API_SERVER}user/refresh_powerbi_dataset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ datasetId: report.datasetId, groupId: report.groupId }),
      });
      setTimeout(() => {
        reportRef.current?.refresh().catch(() => reportRef.current?.reload());
      }, 60000);
    } catch (e) { console.error(e); }
  };

  return (
    <div className="flex flex-col h-full" style={{ background: 'var(--background)' }}>

      {/* ── Toolbar ── */}
      <div className="flex items-center justify-between px-6 lg:px-10 py-3 border-b border-border/60 bg-card flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => history.goBack()}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </button>
          <span className="text-muted-foreground/40">·</span>
          <span className="text-sm font-semibold text-foreground truncate max-w-xs">{report.title || 'Report'}</span>
          {report.isSecure && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-success/10 text-success border border-success/20 font-medium">
              Secure embed
            </span>
          )}
        </div>

        {report.isSecure && (
          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              className="h-8 w-8 rounded-lg border border-border flex items-center justify-center hover:bg-muted transition-colors"
              title="Refresh dataset"
            >
              <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
            </button>

            <div className="relative">
              <button
                onClick={() => setMenuOpen(o => !o)}
                className="h-8 px-3 rounded-lg border border-border flex items-center gap-1.5 text-xs font-medium hover:bg-muted transition-colors"
              >
                <Download className="h-3.5 w-3.5" />
                Export
                <ChevronDown className="h-3 w-3" />
              </button>
              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                  <div className="absolute right-0 top-9 z-20 surface-card rounded-lg border border-border shadow-lg py-1 min-w-[150px]">
                    {['PDF', 'PPTX', 'XLSX'].map(fmt => (
                      <button key={fmt} onClick={() => download(fmt)} className="w-full text-left px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors">
                        Download as {fmt}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Report area ── */}
      {/* Use an explicit calc() height so the embed fills the viewport regardless of ancestor chain.
          The toolbar is ~52px (py-3 border-b). px/py padding adds ~32px. Total offset ~84px. */}
      <div className="px-6 lg:px-10 py-4" style={{ height: 'calc(100vh - 84px)' }}>
        <div className="surface-card rounded-xl overflow-hidden relative" style={{ height: '100%' }}>

          {/* Loading overlay */}
          {!loaded && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-card">
              <img src={imageILogo} alt="IMAGE-I" style={{ width: 160 }} />
              <Loader2 className="h-6 w-6 text-primary animate-spin" />
            </div>
          )}

          {/* Secure embed (Power BI React SDK) */}
          {report.isSecure ? (
            report.embedUrl && report.accessToken && report.reportId ? (
              <PowerBIEmbed
                embedConfig={{
                  type: 'report',
                  id: report.reportId,
                  embedUrl: report.embedUrl,
                  accessToken: report.accessToken,
                  tokenType: models.TokenType.Embed,
                  settings: {
                    layoutType: models.LayoutType.Custom,
                    customLayout: { displayOption: models.DisplayOption.FitToPage },
                    panes: { filters: { visible: false } },
                    navContentPaneEnabled: false,
                  },
                }}
                eventHandlers={new Map([
                  ['loaded',   () => setLoaded(true)],
                  ['rendered', () => setLoaded(true)],
                  ['error',    (event) => { console.error('PowerBI embed error', event?.detail); setLoaded(true); }],
                ])}
                cssClassName="ih-powerbi-embed"
                style={{ opacity: loaded ? 1 : 0, transition: 'opacity 0.3s' }}
                getEmbeddedComponent={(embeddedReport) => { reportRef.current = embeddedReport; }}
              />
            ) : null
          ) : (
            /* Public iframe embed */
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
              {report.embedUrl && (
                <iframe
                  title={report.title || 'Report'}
                  src={report.embedUrl}
                  onLoad={() => setLoaded(true)}
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', opacity: loaded ? 1 : 0, transition: 'opacity 0.3s' }}
                  allowFullScreen
                />
              )}
            </div>
          )}

          {/* Bottom bar cover (hides Power BI footer branding) */}
          <div className="absolute bottom-0 left-0 right-0 h-7 bg-card z-10 pointer-events-none" />
        </div>
      </div>
    </div>
  );
}
